import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Send, Flag, Ban, Trash2, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import ReportMessageDialog from "@/components/ReportMessageDialog";

export default function Messages() {
  const { userId: routeUserId } = useParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null); // { other_user, messages, blocked, i_blocked_them }
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) nav("/login");
  }, [user, loading, nav]);

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/conversations");
      setConversations(data);
    } catch (e) {
      // ignore
    }
  }, []);

  const loadThread = useCallback(async (otherId) => {
    try {
      const { data } = await api.get(`/messages/with/${otherId}`);
      setActive(data);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    if (!user || user === false) return;
    loadConversations();
    const t = setInterval(() => {
      loadConversations();
      if (active?.other_user?.id) loadThread(active.other_user.id);
    }, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, active?.other_user?.id]);

  useEffect(() => {
    if (routeUserId && user && user !== false) {
      loadThread(routeUserId);
    }
  }, [routeUserId, user, loadThread]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || sending || !active) return;
    setSending(true);
    try {
      const { data } = await api.post(`/messages/with/${active.other_user.id}`, { body: input.trim() });
      setActive((a) => a ? { ...a, messages: [...a.messages, data] } : a);
      setInput("");
      loadConversations();
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const blockUser = async () => {
    if (!active) return;
    if (!window.confirm(`Block @${active.other_user.username}? They won't be able to message you.`)) return;
    try {
      await api.post(`/blocks/${active.other_user.id}`);
      toast.success("User blocked");
      loadThread(active.other_user.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const unblockUser = async () => {
    if (!active) return;
    try {
      await api.delete(`/blocks/${active.other_user.id}`);
      toast.success("User unblocked");
      loadThread(active.other_user.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteMsg = async (mid) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/messages/${mid}`);
      loadThread(active.other_user.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!user || user === false) return null;

  return (
    <>
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 lg:py-8">
        <div className="mb-4">
          <div className="text-xs uppercase tracking-widest text-[#857163]">Direct Messages</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-[#3B2C24]">Inbox</h1>
        </div>

        <div className="cp-card overflow-hidden grid grid-cols-1 md:grid-cols-3 min-h-[70vh]" data-testid="messages-layout">
          {/* Conversation list */}
          <aside className={`border-r border-[#EBDCD1] ${active ? "hidden md:block" : ""}`}>
            <div className="px-4 py-3 border-b border-[#EBDCD1] font-semibold text-sm text-[#3B2C24]">
              Conversations ({conversations.length})
            </div>
            <div className="overflow-y-auto max-h-[calc(70vh-49px)]">
              {conversations.length === 0 ? (
                <div className="p-6 text-sm text-[#857163] text-center">
                  No conversations yet. Visit a baker's profile and hit <b>Message</b>.
                </div>
              ) : conversations.map((c) => (
                <button
                  key={c.conversation_id}
                  onClick={() => { loadThread(c.other_user.id); nav(`/messages/${c.other_user.id}`, { replace: false }); }}
                  className={`w-full text-left px-4 py-3 border-b border-[#EBDCD1]/50 hover:bg-[#F9EFE6]/60 transition flex items-center gap-3 ${active?.other_user.id === c.other_user.id ? "bg-[#F9EFE6]/70" : ""}`}
                  data-testid={`conversation-${c.other_user.id}`}
                >
                  <Avatar className="w-10 h-10 border border-[#EBDCD1]">
                    <AvatarImage src={fileUrl(c.other_user.avatar_url)} />
                    <AvatarFallback className="bg-[#FFD07F] text-[#3B2C24] font-semibold text-xs">
                      {c.other_user.username?.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-sm text-[#3B2C24] truncate">@{c.other_user.username}</div>
                      {c.unread_count > 0 && (
                        <span className="bg-[#FF7E67] text-white text-[10px] font-semibold rounded-full px-1.5 min-w-[18px] h-[18px] grid place-items-center">
                          {c.unread_count}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#857163] truncate">{c.last_message.body}</div>
                    <div className="text-[10px] text-[#857163] mt-0.5">
                      {(() => { try { return formatDistanceToNow(new Date(c.last_message.created_at), { addSuffix: true }); } catch { return ""; } })()}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </aside>

          {/* Thread */}
          <section className={`md:col-span-2 flex flex-col ${!active ? "hidden md:flex" : ""}`}>
            {!active ? (
              <div className="flex-1 grid place-items-center text-[#857163] text-sm p-8 text-center">
                Pick a conversation to start.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between px-4 py-3 border-b border-[#EBDCD1]">
                  <div className="flex items-center gap-3">
                    <button onClick={() => setActive(null)} className="md:hidden text-[#857163]"><ChevronLeft className="w-5 h-5" /></button>
                    <Link to={`/u/${active.other_user.username}`} className="flex items-center gap-2 hover:opacity-80">
                      <Avatar className="w-9 h-9 border border-[#EBDCD1]">
                        <AvatarImage src={fileUrl(active.other_user.avatar_url)} />
                        <AvatarFallback className="bg-[#FFD07F] text-[#3B2C24] font-semibold text-xs">
                          {active.other_user.username?.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="font-semibold text-[#3B2C24]">@{active.other_user.username}</div>
                    </Link>
                  </div>
                  <div className="flex items-center gap-1">
                    {active.i_blocked_them ? (
                      <button onClick={unblockUser} className="text-xs px-3 py-1.5 rounded-full bg-[#8EAC50]/15 text-[#8EAC50] hover:bg-[#8EAC50]/25 transition" data-testid="unblock-btn">
                        Unblock
                      </button>
                    ) : (
                      <button onClick={blockUser} className="text-xs px-3 py-1.5 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition inline-flex items-center gap-1" data-testid="block-btn">
                        <Ban className="w-3.5 h-3.5" /> Block
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="messages-thread">
                  {active.messages.length === 0 && (
                    <div className="text-center text-sm text-[#857163] mt-8">
                      Say hi! Your first message starts the conversation.
                    </div>
                  )}
                  {active.messages.map((m) => {
                    const mine = m.sender_id === user.id;
                    return (
                      <div key={m.id} className={`group flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                        <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${
                          mine ? "bg-[#FF7E67] text-white" : "bg-white border border-[#EBDCD1] text-[#3B2C24]"
                        } ${m.is_deleted ? "italic opacity-60" : ""}`}>
                          <div className="whitespace-pre-wrap break-words">{m.body}</div>
                          <div className={`text-[10px] mt-1 flex items-center gap-2 ${mine ? "text-white/70" : "text-[#857163]"}`}>
                            <span>{(() => { try { return formatDistanceToNow(new Date(m.created_at), { addSuffix: true }); } catch { return ""; } })()}</span>
                            {!m.is_deleted && (
                              <div className="opacity-0 group-hover:opacity-100 transition flex gap-1">
                                {mine && (
                                  <button onClick={() => deleteMsg(m.id)} title="Delete" className="hover:text-red-300" data-testid={`msg-delete-${m.id}`}>
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                )}
                                {!mine && (
                                  <ReportMessageDialog messageId={m.id} />
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>

                <form onSubmit={send} className="border-t border-[#EBDCD1] p-3 flex gap-2" data-testid="message-form">
                  {active.i_blocked_them ? (
                    <div className="flex-1 text-xs text-[#857163] py-2 px-3">You've blocked this user. Unblock to send messages.</div>
                  ) : active.blocked ? (
                    <div className="flex-1 text-xs text-red-500 py-2 px-3">You can't send messages in this conversation.</div>
                  ) : (
                    <>
                      <input
                        className="cp-input flex-1"
                        placeholder="Type a message…"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        data-testid="message-input"
                      />
                      <button type="submit" disabled={sending || !input.trim()} className="cp-btn-primary disabled:opacity-50" data-testid="message-send-btn">
                        <Send className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </form>
              </>
            )}
          </section>
        </div>
      </main>
    </>
  );
}
